import React, { useMemo, useState, useEffect } from 'react';
import { Alert, Button, Input, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { createChangeHandler } from 'services/formUtils';
import { SubmitButton } from 'components';
import './CustomLogin.css';

export interface ISetPasswordProps {
  onSubmit: (email: string, password: string) => Promise<void>;
  defaultEmail?: string;
  onEmailChange?: (newValue: string) => void;
  onRestart?: () => void;
  submitButtonLabel: string;
  submitButtonActiveLabel: string;
  hideEmail?: boolean;
}

const SetPassword = (props: ISetPasswordProps) => {
  const { t } = useTranslation('translation');
  // ASCII A-Z / a-z only, to match the backend policy (ASP.NET Identity + the CreateNewUser
  const hasCapitalLetters: RegExp = /[A-Z]/;
  const hasSmallLetters: RegExp = /[a-z]/;
  const hasDigits: RegExp = /[0-9]/;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [showVisualCueForPassword, setShowVisualCueForPassword] = useState(false);
  const [showVisualCueForPasswordRepeat, setShowVisualCueForPasswordRepeat] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (email === '' && props.defaultEmail !== email && props.defaultEmail) {
      setEmail(props.defaultEmail);
    }
  }, [props.defaultEmail]);

  const onSubmit = () => {
    if (!canBeSubmitted) return;
    setIsSubmitting(true);
    props
      .onSubmit(email, password)
      .then(
        () => {
          setIsSubmitting(false);
        },
        () => {
          setIsSubmitting(false);
        }
      )
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  useEffect(() => {
    props.onEmailChange && props.onEmailChange(email);
  }, [email]);

  const passwordIsValid = useMemo(() => {
    return (
      password.length >= 12 &&
      hasDigits.test(password) &&
      hasCapitalLetters.test(password) &&
      hasSmallLetters.test(password)
    );
  }, [password]);

  const passwordMatches = password === repeatPassword;
  const passwordIsNotEmpty = password.length > 0;

  const canBeSubmitted = useMemo(() => {
    return passwordIsValid && passwordMatches && !isSubmitting;
  }, [isSubmitting, passwordIsValid, passwordMatches]);

  const updateVisualCueStatusForPassword = () => {
    setShowVisualCueForPassword(passwordIsNotEmpty && !passwordIsValid);
  };

  const updateVisualCueStatusForPasswordRepeat = () => {
    setShowVisualCueForPasswordRepeat(passwordIsNotEmpty && !passwordMatches);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {!props.hideEmail && (
        <Label>
          {t('login.LABEL_EMAIL')}{' '}
          <Input
            name="email"
            autoComplete="username"
            placeholder={t('login.PROMPT_FOR_EMAIL')}
            value={email}
            type="text"
            onChange={createChangeHandler(setEmail)}
            readOnly={!props.onEmailChange}
          />
        </Label>
      )}
      {showVisualCueForPassword && (
        <Alert color="danger" className="alert-danger">
          {t('login.PASSWORD_FORMAT_INFO')}
        </Alert>
      )}
      <Label>
        {t('login.LABEL_PASSWORD')}{' '}
        <Input
          name="new-password"
          autoComplete="new-password"
          placeholder={t('login.PROMPT_FOR_NEW_PASSWORD')}
          type="password"
          value={password}
          onChange={createChangeHandler(setPassword)}
          onBlur={() => updateVisualCueStatusForPassword()}
        />
      </Label>
      {showVisualCueForPasswordRepeat && (
        <Alert color="danger" className="alert-danger">
          {t('login.PASSWORD_REPEAT_MISSMATCH_INFO')}
        </Alert>
      )}
      <Label>
        {t('login.LABEL_REPEAT_PASSWORD')}{' '}
        <Input
          name="confirm-password"
          autoComplete="new-password"
          placeholder={t('login.PROMPT_FOR_PASSWORD_REPEAT')}
          type="password"
          value={repeatPassword}
          onChange={createChangeHandler(setRepeatPassword)}
          onBlur={() => updateVisualCueStatusForPasswordRepeat()}
        />
      </Label>
      <span className="buttons">
        {props.onRestart && (
          <Button type="button" onClick={props.onRestart}>
            {t('login.SUBMIT_BUTTON_RESTART')}
          </Button>
        )}
        <SubmitButton
          type="submit"
          submitting={isSubmitting ? true : undefined}
          disabled={!canBeSubmitted}
          label={props.submitButtonLabel}
          activelabel={props.submitButtonActiveLabel}
          className="btn-primary"
        />
      </span>
    </form>
  );
};
export default SetPassword;
